import { AnimatePresence, motion, type Variants } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@krea/shared/design-system/input/input-group";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import { useNumericInput } from "@krea/shared/design-system/input/use-numeric-input";
import { Label } from "@krea/shared/design-system/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import type { Currency } from "@krea/shared/types/common";

import type { IFactoringBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

import DetailsItem from "../../../../details/item";

function PercentageInputWithCalc({
  field,
  calculatedAmount,
  formatCurrency,
}: {
  field: {
    name: string;
    value: number | null | undefined;
    onChange: (value: number | null) => void;
    onBlur: () => void;
  };
  calculatedAmount: number;
  formatCurrency: (value: number) => string;
}) {
  const { displayValue, handleFocus, handleChange, handleBlur } =
    useNumericInput({
      value: field.value ?? null,
      onChange: (v) => field.onChange(v),
      onBlur: () => field.onBlur(),
      type: "float",
    });

  return (
    <InputGroup>
      <InputGroupInput
        id={field.name}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode="decimal"
        placeholder="Value in %"
      />
      <InputGroupAddon align="inline-end">
        <InputGroupText>%</InputGroupText>
        <AnimatePresence>
          {!!calculatedAmount && (
            <motion.span
              className="inline-flex items-center gap-1.5"
              initial={{ opacity: 0, width: 0 }}
              animate={{
                opacity: 1,
                width: "auto",
                transition: { type: "tween", duration: 0.1, ease: "easeOut" },
              }}
              exit={{
                opacity: 0,
                width: 0,
                transition: { type: "tween", duration: 0.1, ease: "easeOut" },
              }}
            >
              <span aria-hidden className="h-3 w-px shrink-0 bg-gray-200" />
              Calc: {formatCurrency(calculatedAmount)}
            </motion.span>
          )}
        </AnimatePresence>
      </InputGroupAddon>
    </InputGroup>
  );
}

const withRegressInputVariants: Variants = {
  initial: {
    opacity: 0,
    y: -24,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", duration: 0.2, bounce: 0.15 },
  },
  exit: {
    opacity: 0,
    y: -24,
    transition: { type: "spring", duration: 0.2, bounce: 0.15 },
  },
};

function RegressRadioGroup({
  name,
  value,
  onChange,
  isSubmitting,
}: {
  name: string;
  value: string;
  onChange: (v: boolean) => void;
  isSubmitting: boolean;
}) {
  const yesId = name;
  const noId = `${name}-no`;

  return (
    <RadioGroup
      onValueChange={(v) => onChange(v === "true")}
      value={value}
      disabled={isSubmitting}
      className="flex flex-row gap-4"
    >
      <div className="flex items-center gap-2">
        <RadioGroupItem value="true" id={yesId} />
        <Label htmlFor={yesId} className="cursor-pointer font-normal">
          Yes
        </Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value="false" id={noId} />
        <Label htmlFor={noId} className="cursor-pointer font-normal">
          No
        </Label>
      </div>
    </RadioGroup>
  );
}

interface LoanFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const FactoringForm = ({
  bidFormIndex,
  currency,
  isEditing,
}: LoanFormProps) => {
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<IFactoringBidInput>>();

  const bid = useWatch({
    control,
    name: `bids.${bidFormIndex}`,
  });

  const [formattedCurrencyValue] = useFormatCurrency({
    currency,
  });

  const calculatedPurchasedAmount =
    ((bid.purchaseLimit ?? 0) * (bid.purchasedAmountPercent ?? 0)) / 100;

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={isEditing ? "Update factoring terms" : "Factoring terms"}
          className="mb-4"
        >
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.purchaseLimit`}
              label="Purchase limit"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.purchasedAmountPercent`}
              label="Financing rate (in %)"
              required
              render={({ field }) => (
                <PercentageInputWithCalc
                  field={field}
                  calculatedAmount={calculatedPurchasedAmount}
                  formatCurrency={formattedCurrencyValue}
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.commissionPercent`}
              label="Credit fee (per indicated number of 'Payment terms days' below)"
              required
              render={({ field }) => (
                <PercentageInput {...field} id={field.name} />
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-4">
          <div className="w-full">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paymentTermsDays`}
              label="Payment term days"
              required
              render={({ field }) => (
                <InputGroup>
                  <IntegerInput
                    {...field}
                    id={field.name}
                    className="flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>days</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-4">
          <div className="w-full">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.withRegress`}
              label="Regress"
              required
              render={({ field }) => {
                let radioValue = "";
                if (field.value === true) radioValue = "true";
                else if (field.value === false) radioValue = "false";

                return (
                  <RegressRadioGroup
                    name={field.name}
                    value={radioValue}
                    onChange={(v) => {
                      field.onChange(v);
                      if (!v) {
                        setValue(
                          `bids.${bidFormIndex}.regressNumDaysAfterDueDate`,
                          undefined,
                        );
                      }
                    }}
                    isSubmitting={isSubmitting}
                  />
                );
              }}
            />
          </div>
        </DetailsItem>

        <AnimatePresence mode="wait" initial={false}>
          {bid.withRegress && (
            <DetailsItem className="mb-4">
              <div className="w-full">
                <motion.div
                  key="withRegress"
                  initial="initial"
                  exit="exit"
                  animate="animate"
                  variants={withRegressInputVariants}
                >
                  <ControlledWrapper
                    control={control}
                    name={`bids.${bidFormIndex}.regressNumDaysAfterDueDate`}
                    label="Regress number of days"
                    required
                    render={({ field }) => (
                      <InputGroup>
                        <IntegerInput
                          {...field}
                          id={field.name}
                          className="flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0"
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>days</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    )}
                  />
                </motion.div>
              </div>
            </DetailsItem>
          )}
        </AnimatePresence>

        <DetailsItem className="mb-4" title="">
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.handlingFeePerInvoice`}
              label="Handling fee per invoice"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.monthlyFee`}
              label="Monthly fee"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-4" title="">
          <div className="w-full">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.limitFeePerYearPercent`}
              label="Limit fee per year"
              render={({ field }) => (
                <PercentageInput {...field} id={field.name} />
              )}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
